import { createAgnosticHttpClient } from 'auth/infra/createAgnosticHttpClient'

import { type Tractor } from '../domain/Tractor'
import { CarrierRepository } from '../infra/CarrierRepository'
import { ShipmentRepository } from '../infra/ShipmentRepository'

export type CreateAndAssignTractorNumberDependencies = {
  carrierRepository: CarrierRepository
  shipmentRepository: ShipmentRepository
}

export type CreateAndAssignTractorNumberParams = {
  carrierUUID: string
  shipmentUUID: string
  tractorNumber: string
}

export function createAndAssignTractorNumberFactory({
  carrierRepository,
  shipmentRepository,
}: CreateAndAssignTractorNumberDependencies) {
  return async function createAndAssignTractorNumber({
    carrierUUID,
    shipmentUUID,
    tractorNumber,
  }: CreateAndAssignTractorNumberParams): Promise<Tractor> {
    let tractor = undefined
    try {
      tractor = await carrierRepository.createCarrierTractorNumber({
        carrierUUID,
        tractorNumber,
      })
    } catch (error) {
      throw new Error('Failed to create the truck', { cause: error as Error })
    }

    try {
      await shipmentRepository.updateShipmentTractorNumber({
        shipmentUUID,
        tractorNumber: tractor.tractorNumber,
      })
    } catch (error) {
      throw new Error('Failed to add tractor to the shipment', { cause: error as Error })
    }

    return tractor
  }
}

export function createAndAssignTractorNumber({
  carrierUUID,
  shipmentUUID,
  tractorNumber,
}: CreateAndAssignTractorNumberParams) {
  const httpClient = createAgnosticHttpClient()
  const carrierRepository = new CarrierRepository({ httpClient })
  const shipmentRepository = new ShipmentRepository({ httpClient })
  const createAndAssign = createAndAssignTractorNumberFactory({
    carrierRepository,
    shipmentRepository,
  })

  return createAndAssign({ carrierUUID, shipmentUUID, tractorNumber })
}
